import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import moment from 'moment';
import {
  useNavigate,
} from 'react-router';
import {
  get,
  find,
  toNumber,
  uniqueId,
  uniqWith,
} from 'lodash';
import {
  API,
  GlobalActions,
  ToastActions,
  LocationActions,
  LocationHelper,
  NavigationHelper,
  VibeModal,
  VibeButton,
  VibeIcon,
  viArchive,
  viCopy,
  color,
} from 'vibeguide';
import Grid from '@mui/material/Grid';
import LocationSidebar from '../../../../Sidebar/LocationSidebar';
import HdmiSettings from '../../../../Sidebar/HdmiSettings';
import ProfileContent from '../ProfileContent';
import ProfileInfo from '../ProfileInfo';
import ProfileAddress from '../ProfileAddress';
import ProfileDisplayInformation from '../ProfileDisplayInformation';
import ProfileSettings from '../ProfileSettings';
import ProfileFooter from '../ProfileFooter';
import ThirdPartyData from '../../../ThirdPartyData/ThirdPartyData';
import ExternalInfo from '../../../ThirdPartyData/ExternalInfo';
import ProfileExternalIds from '../ProfileExternalIds';
import './DuplicateIDModal.scss';

function ProfileInformation({
  className = '',
  style = {},
  location,
  user,
  setPanel,
  queueToast,
  updateCurrentLocation,
  onUpdate,
  isDisabled,
}) {
  const [hasChanges, setHasChanges] = useState(false);
  const [showDuplicateModal, setShowDuplicateModal] = useState(false);
  const [selectedThirdPartyProvider, setSelectedThirdPartyProvider] = useState('');
  const [showConfirmArchive, setShowConfirmArchive] = useState(false);
  const [newExternalIDs, setNewExternalIDs] = useState([]);
  const [duplicateArray, setDuplicateArray] = useState([]);
  const [confirmationCode, setConfirmationCode] = useState(null);
  const [confirmationMessage, setConfirmationMessage] = useState(null);
  const [useExternalIds, setUseExternalIds] = useState([]);

  const history = useNavigate();
  const qs = NavigationHelper.getParams();
  const isActive = get(location, 'active', false);

  useEffect(() => {
    setUseExternalIds(get(location, 'externalIds', []).map(externalId => {
      if (externalId._id) {
        return externalId;
      }

      return {
        _id: uniqueId('external-id-'),
        ...externalId,
      };
    }));
  }, [location.externalIds]);

  /**
   * The location object was changed
   */
  const onChange = (data) => {
    setHasChanges(true);
    onUpdate(data);
  };

  /**
   * The selected third party provider has changed
   */
  const onChangeThirdPartyProvider = (providerName) => {
    setSelectedThirdPartyProvider(providerName);
  };

  /**
   * Refresh the location data after save
   */
  const refreshLocationData = async () => {
    const locationObj = await API.Location.getById(location._id);
    onUpdate(locationObj);
    updateCurrentLocation(locationObj);
    setHasChanges(false);
  };

  /**
   * Get the active third party data item row
   */
  const getActiveThirdPartyItem = () => {
    const currDate = new moment();
    // get the item that is currently active based on start/end dates
    const activeItem = find(location.thirdPartyData, (item) => {
      // to make sure it's in between the dates use the end of the day for the stop date
      const endDate = moment(item.endDate).endOf('day');
      return currDate.isBetween(item.startDate, endDate);
    });

    return activeItem;
  };

  /**
   * Information Panel
   */
  const getInformation = () => {
    return (
      <ProfileContent title="Information">
        <ProfileInfo
          name={get(location, 'name', '')}
          companyId={get(location, 'companyId', '')}
          companyName={get(location, 'companyName', '')}
          bannerId={get(location, 'bannerId', '')}
          bannerName={get(location, 'bannerName', '')}
          contentRating={get(location, 'contentRating', '')}
          mediaFormat={get(location, 'mediaFormat', '')}
          zoneId={get(location, 'zoneId', '')}
          zoneName={get(location, 'zoneName', '')}
          impressionMultiplier={Math.floor((toNumber(get(location, 'impressionMultiplier', 0)) * 100))}
          category={get(location, 'category', '')}
          salesforceId={get(location, 'salesforceId', '')}
          testLocation={get(location, 'testLocation', '')}
          tags={get(location, 'tags', [])}
          isAdmin={user.sysAdmin}
          canEdit={user.can('location.modify')}
          canAssignTag={user.can('location.assign_tag')}
          onUpdate={onChange}
        />
      </ProfileContent>
    );
  };

  /**
   * Information Panel
   */
  const getDisplayInformation = () => {
    // show display information is format is visual
    return get(location, 'mediaFormat', '') === 'visual'
      ? (
        <>
          <ProfileContent title="">
            <ProfileDisplayInformation
              displaySettings={get(location, 'displaySettings', undefined)}
              isAdmin={user.sysAdmin}
              canEdit={user.can('location.modify')}
              onUpdate={onChange}
            />
          </ProfileContent>
          {user.can('location.manage_display_interface_override') && (
            <ProfileContent title="">
              <HdmiSettings
                className="ProfileHdmiSettings"
                displaySettings={get(location, 'displaySettings', undefined)}
                canEdit={user.can('location.modify')}
                onUpdate={onChange}
              />
            </ProfileContent>
          )}
        </>
      ) : null;
  };

  /**
   * Address Panel
   */
  const getAddress = () => {
    return (
      <ProfileContent title="Address">
        <ProfileAddress
          country={get(location, 'country', '')}
          address1={get(location, 'address1', '')}
          address2={get(location, 'address2', '')}
          city={get(location, 'city', '')}
          state={get(location, 'state', '')}
          province={get(location, 'province', '')}
          postalCode={get(location, 'postalCode', '')}
          geoCoordinatesLocked={get(location, 'geoCoordinatesLocked', true)}
          lat={get(location, 'lat', 0)}
          long={get(location, 'long', 0)}
          timezone={get(location, 'timeZone', '')}
          locality={get(location, 'locality', {})}
          isAdmin={user.sysAdmin}
          canEdit={user.can('location.modify')}
          onUpdate={onChange}
        />
      </ProfileContent>
    );
  };

  /**
   * Settings Panel
   */
  const getSettings = () => {
    return (
      <ProfileContent title="Ad Information">
        <ProfileSettings
          companyId={get(location, 'companyId', '')}
          contentRating={get(location, 'contentRating', '')}
          mediaFormat={get(location, 'mediaFormat', '')}
          contentDeliverySystem={get(location, 'contentDeliverySystem', '')}
          contentNetworkOwner={get(location, 'contentNetworkOwner', '')}
          adDeliverySystem={get(location, 'adDeliverySystem', '')}
          adNetworkOwner={get(location, 'adNetworkOwner', '')}
          adProgramEnabled={get(location, 'adProgramEnabled', false)}
          adNetworkEnabled={get(location, 'adNetworkEnabled', false)}
          openExchangeEnabled={get(location, 'openExchangeEnabled', false)}
          isAdmin={user.sysAdmin}
          canEdit={user.can('location.modify')}
          canViewCompany={user.can('company.view')}
          canEnableAdProgram={user.can('location-ad-program.modify')}
          canModifyLocationAdProgram={user.can('location-ad-program.modify')}
          onUpdate={onChange}
        />
      </ProfileContent>
    );
  };

  /**
   * Check if the user can save changes
   */
  const canSave = () => {
    if (!isActive) {
      // location is archived, prevent saving changes
      return false;
    }

    if (user.can('location.modify')
      || user.can('location.manage_external_ids')
    ) {
      return true;
    }

    return false;
  };

  /**
   * When the user clicks the unarchive button
   */
  const onClickUnarchive = async () => {
    const response = await API.Location.reactivate({
      _id: location._id,
    });

    const success = get(response, '[0].type') === 'LOCATION.REACTIVATED';

    if (success) {
      updateCurrentLocation({
        active: true,
      });

      onUpdate({
        ...location,
        active: true,
      });
    }
  };

  /**
   * When the user clicks the archive button
   */
  const onClickArchive = async () => {
    setShowConfirmArchive(true);

    const { code, message } = await LocationHelper.archiveLocation(location);
    setConfirmationCode(code);
    setConfirmationMessage(message);
  };

  /**
   * Archive the location
   */
  const onConfirmArchive = async () => {
    const responseDeleteComplete = await API.Location.deactivate(location._id,
      { headers: { 'VAPI-ConfirmationCode': confirmationCode } });

    const successText = 'LOCATION.DEACTIVATED';
    const success = get(responseDeleteComplete, '[0].type') === successText;

    if (success) {
      setShowConfirmArchive(false);
      setConfirmationCode(null);
      setConfirmationMessage(null);
      history('/locations');
    }
  };

  /**
   * Cancel archiving the location
   */
  const onCancelArchive = () => {
    setShowConfirmArchive(false);
    setConfirmationCode(null);
    setConfirmationMessage(null);
  };

  /**
   * When the sidebar is closed
   */
  const onCloseSidebar = () => {
    const url = NavigationHelper.updateParams({
      type: null,
    });

    history(url);
  };

  /**
   * Open the sidebar
   */
  const onOpenSidebar = () => {
    setPanel({
      show: true,
      backdrop: true,
      children: (
        <LocationSidebar
          locationId={location._id}
          isNew
        />
      ),
      onClose: onCloseSidebar,
    });
  };

  /**
   * Close Duplicate Modal
   */
  const closeDuplicateModal = () => {
    setShowDuplicateModal(false);
  };

  /**
   * Save the location profile
   */
  const onSave = async () => {
    // data object to send to the API to save the location
    let data = {};
    const modifyTags = location.tags.filter(tag => tag.status === 'add' || tag.status === 'remove');
    let externalIdsError = false;

    if (user.can('location.modify') && user.sysAdmin) {
      // Update entire location profile
      const baseDisplaySettings = location.displaySettings || {};

      // Form inputs allow empty strings for UX, so coerce numeric display
      // settings to finite numbers right before sending to the API.
      const toFiniteNumber = (value) => {
        const num = toNumber(value);
        return Number.isFinite(num) ? num : 0;
      };
      const toNumericPair = (pair) => [
        toFiniteNumber(pair?.[0]),
        toFiniteNumber(pair?.[1]),
      ];
      // Screen dimensions accept empty strings as "unset" — the API rejects 0
      // but allows '' — so preserve blanks instead of coercing to 0.
      const toScreenDimensionValue = (value) => {
        if (value === '' || value === undefined || value === null) {
          return '';
        }
        const num = toNumber(value);
        return Number.isFinite(num) ? num : '';
      };
      const toScreenDimensionsPair = (pair) => [
        toScreenDimensionValue(pair?.[0]),
        toScreenDimensionValue(pair?.[1]),
      ];
      // screenDiagonal follows the same "blank means unset" rules as
      // screenDimensions — the API rejects 0 but accepts a missing field.
      const toScreenDiagonalValue = (value) => {
        if (value === '' || value === undefined || value === null) {
          return '';
        }
        const num = toNumber(value);
        return Number.isFinite(num) ? num : '';
      };

      const resolutionSize = toNumericPair(baseDisplaySettings.resolution?.size);
      const screenDimensionsPair = toScreenDimensionsPair(baseDisplaySettings.screenDimensions);
      const screenDiagonalValue = toScreenDiagonalValue(baseDisplaySettings.screenDiagonal);

      const resolvedDisplaySettings = location.mediaFormat === 'visual'
        ? {
          ...baseDisplaySettings,
          resolution: baseDisplaySettings.resolution
            ? {
              ...baseDisplaySettings.resolution,
              size: resolutionSize,
            }
            : baseDisplaySettings.resolution,
          screenDimensions: screenDimensionsPair,
          screenDiagonal: screenDiagonalValue,
          // Always send a display area: when advanced is off it mirrors the
          // resolution at the origin so the API has unique resolutions to
          // work with.
          displayAreaDimensions: toNumericPair(
            baseDisplaySettings.advancedEnabled
              ? baseDisplaySettings.displayAreaDimensions
              : resolutionSize,
          ),
          displayAreaPosition: toNumericPair(
            baseDisplaySettings.advancedEnabled
              ? baseDisplaySettings.displayAreaPosition
              : [0, 0],
          ),
          displayInterfaceOverrideEnabled: Boolean(
            baseDisplaySettings.displayInterfaceOverrideEnabled,
          ),
          displayInterfaceOverrideResolution: toNumericPair(
            baseDisplaySettings.displayInterfaceOverrideResolution || [0, 0],
          ),
        }
        : null;

      // The API rejects 0 for screenDimensions / screenDiagonal and treats a
      // missing field as "unset", so drop the fields entirely when blank.
      if (
        resolvedDisplaySettings
        && screenDimensionsPair[0] === ''
        && screenDimensionsPair[1] === ''
      ) {
        delete resolvedDisplaySettings.screenDimensions;
      }
      if (resolvedDisplaySettings && screenDiagonalValue === '') {
        delete resolvedDisplaySettings.screenDiagonal;
      }

      const visualData = resolvedDisplaySettings
        ? { displaySettings: resolvedDisplaySettings }
        : {};

      const includeData = {};

      if (location.impressionMultiplier > 0) {
        // include impression multiplier data
        includeData.impressionMultiplier = location.impressionMultiplier;
      }

      data = {
        _id: location._id,
        name: location.name,
        companyId: location.companyId,
        category: location.category,
        address1: location.address1,
        address2: location.address2 || '',
        country: location.country,
        city: location.city,
        state: location.state,
        postalCode: location.postalCode,
        bannerId: location.bannerId,
        bannerName: location.bannerName,
        contentRating: location.contentRating,
        geoCoordinatesLocked: location.geoCoordinatesLocked,
        salesforceId: location.salesforceId,
        testLocation: location.testLocation,
        mediaFormat: location.mediaFormat,
        zoneId: location.zoneId,
        adDeliverySystem: location.adDeliverySystem,
        adNetworkOwner: location.adNetworkOwner,
        contentDeliverySystem: location.contentDeliverySystem,
        contentNetworkOwner: location.contentNetworkOwner,
        adProgramEnabled: location.adProgramEnabled,
        adNetworkEnabled: location.adNetworkEnabled,
        openExchangeEnabled: location.openExchangeEnabled,
        lat: toNumber(location.lat),
        long: toNumber(location.long),
        tags: modifyTags.map((tag) => {
          return {
            _id: tag._id,
            action: tag.status,
          };
        }),
        ...visualData,
        ...includeData,
      };
    } else if (user.can('location.modify')) {
      // Only allow non-admins to update rating, tags, and image
      data = {
        _id: location._id,
        contentRating: location.contentRating,
        tags: modifyTags.map((tag) => {
          return {
            _id: tag._id,
            action: tag.status,
          };
        }),
      };
    }

    if (user.can('location.manage_third_party_data')) {
      data.thirdPartyData = {
        ...location.thirdPartyData,
        // ensure there is a data array when sending up third party data
        data: get(location, 'thirdPartyData.data', []),
      };
    }

    queueToast({
      type: 'info',
      title: 'Saving Location...',
      allowClose: true,
    });

    const tempSavedIDs = [];

    if (user.can('location.manage_external_ids')) {
      try {
        // If duplicates, throw warning modal
        const duplicateArray1 = [];

        newExternalIDs.forEach((newID) => {
          useExternalIds.forEach((extID) => {
            if (newID.source === extID.source && newID.extId === extID.extId) {
              duplicateArray1.push(newID);
            }
          });
        });

        const extIdMap = {};
        const newExtIdDuplicates = [];

        newExternalIDs.forEach(item => {
          const extIdValue = item.extId;
          const sourceValue = item.source;
          if (extIdMap[extIdValue] && extIdMap[sourceValue]) {
            newExtIdDuplicates.push(item);
          } else {
            extIdMap[extIdValue] = true;
            extIdMap[sourceValue] = true;
          }
        });

        if (duplicateArray1.length > 0 || newExtIdDuplicates.length > 0) {
          // Set to get unique objects
          const setObj = new Set(duplicateArray1.map(JSON.stringify));
          const dupeArray = Array.from(setObj).map(JSON.parse);
          const fullDuplicateArray = [
            ...newExtIdDuplicates,
            ...dupeArray,
          ];

          const uniqueExtIDArray = uniqWith(fullDuplicateArray, (extIdObj1, extIdObj2) => {
            if (extIdObj1.extId === extIdObj2.extId && extIdObj1.source === extIdObj2.source) {
              return true;
            }
            return false;
          });
          // show duplicate modal
          setDuplicateArray(uniqueExtIDArray);
          setShowDuplicateModal(true);
          return;
        }

        // eslint-disable-next-line max-len
        const savedExternalIds = await Promise.all(newExternalIDs.filter(id => id.source && id.extId).map(async (id) => {
          const externalID = API.Location.addExternalID({
            id: location._id,
            source: id.source,
            extid: id.extId,
          });
          const promiseResult = await Promise.resolve(externalID);
          tempSavedIDs.push(promiseResult[0].data);
          return promiseResult;
        }));

        savedExternalIds.forEach((extid) => {
          if (extid[0].type === 'LOCATION.EXTERNAL_ID_REGISTERED') {
            queueToast({
              type: 'success',
              title: 'External ID Saved!',
              allowClose: true,
              delay: 500,
            });
          }
        });

        setNewExternalIDs(newExternalIDs.map(extId => {
          extId.saving = true;
          return extId;
        }));

        if (!user.can('location.modify')) {
          // refresh location data if the user can only update external IDs
          refreshLocationData();
        }
      } catch (err) {
        console.error('Error Saving External IDs', err);

        externalIdsError = true;

        const filteredExtIDs = newExternalIDs.filter(newExtID => {
          if (err.includes(newExtID.source)) {
            newExtID.error = true;
          }
          return newExtID;
        });

        setNewExternalIDs(filteredExtIDs);

        queueToast({
          type: 'error',
          title: 'Error Saving External IDs',
          timeout: 10,
          allowClose: true,
        });
      }
    }

    if (user.can('location.modify')) {
      try {
        const updateResponse = await API.Location.update(data);
        const success = get(updateResponse, '[0].type') === 'LOCATION.UPDATED';

        if (success) {
          queueToast({
            type: 'success',
            title: 'Saved Location!',
            allowClose: true,
            delay: 500,
          });

          refreshLocationData();
        } else {
          queueToast({
            type: 'error',
            title: 'Error Saving Location',
            timeout: 10,
            allowClose: true,
          });
        }
      } catch (err) {
        console.error('Error Saving Location', err);

        queueToast({
          type: 'error',
          title: 'Error Saving Location',
          timeout: 10,
          allowClose: true,
        });
      }
    }
  };

  /**
   * Open the sidebar to duplicate the location
   */
  useEffect(() => {
    if (qs.type === 'new') {
      onOpenSidebar();
    }
  }, [qs.type]);

  return (
    <div
      className={classNames('ProfileInformation', className)}
      style={{
        flexGrow: 1,
        ...style,
      }}
    >
      <div
        style={{
          height: 'calc(100% - 68px)',
          padding: 24,
          paddingBottom: 0,
        }}
      >
        {user.can('location.view_third_party_data') || user.can('location.view_external_ids') ? (
          <Grid spacing={2} container>
            <Grid size={{ xs: 12, lg: 4 }}>
              {getInformation()}
              {getSettings()}
              {getDisplayInformation()}
              {getAddress()}
            </Grid>

            <Grid size={{ xs: 12, lg: 8 }}>
              {user.can('location.view_external_ids') && (
                <ProfileContent title="External Source ID's">
                  <ProfileExternalIds
                    externalIds={useExternalIds}
                    locationId={location._id}
                    setHasChanges={setHasChanges}
                    newExternalIDs={newExternalIDs}
                    setNewExternalIDs={setNewExternalIDs}
                    onUpdate={onUpdate}
                    updateCurrentLocation={updateCurrentLocation}
                  />
                </ProfileContent>
              )}

              {user.can('location.view_third_party_data') && (
                <>
                  <ProfileContent title="Third Party Data">
                    <ThirdPartyData
                      thirdPartyData={get(location, 'thirdPartyData', {})}
                      onChangeThirdPartyProvider={onChangeThirdPartyProvider}
                      onUpdate={onChange}
                    />
                  </ProfileContent>

                  <ProfileContent title="External ID API Info">
                    <ExternalInfo
                      selectedThirdPartyProvider={selectedThirdPartyProvider}
                      activeThirdPartyItem={getActiveThirdPartyItem()}
                    />
                  </ProfileContent>
                </>
              )}
            </Grid>
          </Grid>
        ) : (
          <Grid spacing={2} container>
            <Grid size={{ xs: 12, lg: 4 }}>
              {getInformation()}
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              {getSettings()}
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              {getDisplayInformation()}
            </Grid>

            <Grid size={{ xs: 12, lg: 4 }}>
              {getAddress()}
            </Grid>
          </Grid>
        )}
      </div>

      <ProfileFooter>
        {canSave() && (
          <VibeButton
            text="Save Changes"
            color="secondary"
            disabled={isDisabled || !hasChanges || (location.adProgramEnabled && !location.adNetworkEnabled)
              || (location.openExchangeEnabled && (!location.adNetworkEnabled || !location.adProgramEnabled))}
            testId="location-profile-save-button"
            onClick={onSave}
          />
        )}

        {user.can('location.create') && isActive && (
          <VibeIcon
            icon={viCopy}
            type="button"
            buttonProps={{
              size: 32,
              borderColor: color.primary,
              style: {
                marginLeft: 8,
              },
            }}
            color={color.primary}
            size={20}
            tooltip="Duplicate"
            link={NavigationHelper.updateParams({
              type: 'new',
            })}
            data-testid="location-profile-duplicate-button"
          />
        )}

        {user.can('location.delete') && (
          <VibeIcon
            icon={viArchive}
            type="button"
            buttonProps={{
              size: 32,
              borderColor: isActive
                ? color.error
                : color.success,
              style: {
                marginLeft: 8,
              },
            }}
            tooltip={isActive
              ? 'Archive'
              : 'Unarchive'}
            color={isActive
              ? color.error
              : color.success}
            size={20}
            onClick={isActive
              ? onClickArchive
              : onClickUnarchive}
            data-testid={isActive ? 'location-profile-archive-button' : 'location-profile-unarchive-button'}
          />
        )}
      </ProfileFooter>

      <VibeModal
        show={showConfirmArchive}
        type="confirm"
        title="Archive"
        confirmProps={{
          color: 'error',
        }}
        testIds={{
          root: 'archive-location-profile-dialog',
          confirm: 'archive-location-profile-confirm-button',
          cancel: 'archive-location-profile-cancel-button',
        }}
        onConfirm={onConfirmArchive}
        onClose={onCancelArchive}
      >
        {confirmationMessage}
      </VibeModal>

      <VibeModal
        className="DuplicateIDModal"
        type="custom"
        show={showDuplicateModal}
        text={(
          <div>
            <div className="external-delete-title">
              ID is already in use.
            </div>
            <div className="external-delete-text">
              There are one or more IDs already in use, or you are attempting to upload duplicate source/id values.
            </div>
            <div className="external-duplicates-list">
              {duplicateArray.map((dupe) => (
                <div className="external-duplicates-row" key={dupe._id}>
                  <div className="external-duplicates-source">{dupe.source}</div>
                  <div className="external-duplicates-extid">{dupe.extId}</div>
                </div>
              ))}
            </div>
          </div>
        )}
        testIds={{
          root: 'location-duplicate-id-dialog',
          confirm: 'location-duplicate-id-confirm-button',
          cancel: 'location-duplicate-id-cancel-button',
        }}
        onClose={closeDuplicateModal}
      />
    </div>
  );
}

ProfileInformation.propTypes = {
  className: PropTypes.string,
  style: PropTypes.object,
  location: PropTypes.object.isRequired,
  user: PropTypes.object.isRequired,
  onUpdate: PropTypes.func.isRequired,
};

const mapDispatchToProps = {
  queueToast: ToastActions.queueToast,
  setPanel: GlobalActions.setPanel,
  updateCurrentLocation: LocationActions.updateCurrentLocation,
};

export default connect(null, mapDispatchToProps)(ProfileInformation);
